import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useImpersonation } from '@/contexts/ImpersonationContext';
import tacticalLogo from '@/assets/tactical-tax-logo.png';
import SubscriptionLocked from './SubscriptionLocked';

const ACTIVE_STATUSES = ['active', 'trialing', 'exempt'];

interface ProtectedRouteProps {
  children: React.ReactNode;
  /** If set, user must have at least one of these roles */
  allowedRoles?: string[];
  /** @deprecated Use allowedRoles instead */
  requireAdmin?: boolean;
}

export default function ProtectedRoute({ children, allowedRoles, requireAdmin = false }: ProtectedRouteProps) {
  const { user, loading, slowLoad, roles, org, profile, isSuperAdmin, isPlatformAdmin, isTrialExpired } = useAuth();
  const { isImpersonating } = useImpersonation();
  const [orgGrace, setOrgGrace] = useState(false);

  // Grace period: when user exists but org is momentarily null (re-fetch in
  // progress), show a brief loading spinner instead of SubscriptionLocked.
  useEffect(() => {
    if (!user || loading || isSuperAdmin || org) {
      setOrgGrace(false);
      return;
    }
    // Signed-in, non-super-admin user temporarily missing an org — brief grace period
    setOrgGrace(true);
    const timer = setTimeout(() => setOrgGrace(false), 2_000);
    return () => clearTimeout(timer);
  }, [loading, user, org, isSuperAdmin]);

  // Synchronous guard: don't evaluate subscription gate until roles are resolved
  const rolesResolved = roles.length > 0 || !user;

  // Incomplete signup: profile exists but invite was never redeemed (no org, no roles).
  // This happens when the browser closes or redeem_invite fails between signUp and
  // redeem completing. Show a recovery message instead of an infinite loading spinner.
  // Excluded during impersonation: an admin viewing another user shouldn't get bounced
  // here if the impersonated user's roles/org failed to load — that's a data fetch
  // problem, surfaced by ImpersonationContext, not a real incomplete-signup state.
  const incompleteSignup = !loading && !orgGrace && user && !org && roles.length === 0 && !isSuperAdmin && !isImpersonating;

  if (incompleteSignup) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4 max-w-sm text-center">
          <img src={tacticalLogo} alt="Tactical Tax Genie" className="h-10 w-10 object-contain" style={{ borderRadius: 0 }} />
          <h2 className="text-lg font-semibold">Account Setup Incomplete</h2>
          <p className="text-sm text-muted-foreground">
            Your account was created but setup didn't finish. Click the invite link in your email to complete setup, or contact your administrator for a new invite.
          </p>
        </div>
      </div>
    );
  }

  if (loading || orgGrace || (user && !rolesResolved)) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <img src={tacticalLogo} alt="Tactical Tax Genie" className="h-10 w-10 animate-pulse object-contain" style={{ borderRadius: 0 }} />
          <p className="text-sm text-muted-foreground">Loading…</p>
          {slowLoad && (
            <p className="text-sm text-muted-foreground">Taking longer than expected…</p>
          )}
        </div>
      </div>
    );
  }

  if (!user) return <Navigate to="/auth" replace />;

  // Legacy requireAdmin support
  if (requireAdmin && !isSuperAdmin) return <Navigate to="/" replace />;

  // Role-based gating
  if (allowedRoles && allowedRoles.length > 0) {
    const hasRole = roles.some(r => allowedRoles.includes(r));
    if (!hasRole) return <Navigate to="/" replace />;
  }

  // Subscription gate — super_admins, platform_admins, and impersonating admins
  // bypass every check below (internal staff don't have customer-style subscriptions).
  if (!isSuperAdmin && !isPlatformAdmin && !isImpersonating) {
    // 1. Account suspension (billing) or deactivation (20260929000002: off
    //    every screen; the lock screen offers "Reactivate my account" inside
    //    the 30-day self-service window).
    if (profile?.status === 'suspended') {
      return <SubscriptionLocked reason="suspended" />;
    }
    if (profile?.status === 'deactivated') {
      return <SubscriptionLocked reason="deactivated" />;
    }

    // 2. The database lets this member in (their org or one above it is
    //    active, exempt, or in trial) — so does the app. Only when that
    //    answer is missing does the org's own status decide below.
    if (org?.chain_subscription_valid === true) return <>{children}</>;

    // 3. Trial period expired (even if subscription_status is still 'trialing')
    if (org?.subscription_status === 'trialing' && isTrialExpired) {
      return <SubscriptionLocked reason="trial_expired" />;
    }

    // 4. Standard subscription gate
    const status = org?.subscription_status;
    if (!status || !ACTIVE_STATUSES.includes(status)) {
      return <SubscriptionLocked />;
    }
  }

  return <>{children}</>;
}
